// src/pages/BlogPost.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/BlogPost.css';

// Default author image URL
const DEFAULT_AUTHOR_IMAGE = "https://legate-blog-content.s3.us-west-2.amazonaws.com/images/legate-team.png";

const BlogPost = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  
  useEffect(() => {
    // Reset state when post ID changes
    setLoading(true);
    setPost(null);
    setError(null);
    
    const fetchBlogPost = async () => {
      try {
        // First fetch the blog index from API server to get all posts
        const indexResponse = await axios.get('/api/blogs');
        const allPosts = indexResponse.data || [];
        
        // Find the current post in the index
        const currentPost = allPosts.find(p => p.id === postId);
        
        if (!currentPost) {
          setError('Blog post not found');
          setLoading(false);
          return;
        }
        
        // Find related posts (same category, excluding current post)
        const related = allPosts
          .filter(p => p.category === currentPost.category && p.id !== postId)
          .slice(0, 3);
        
        setRelatedPosts(related);
        
        // Fetch the full blog post content from API server
        const postResponse = await axios.get(`/api/blogs/${postId}`);
        setPost(postResponse.data);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Failed to load blog post. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchBlogPost();
  }, [postId]);
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading article...</p>
      </div>
    );
  }
  
  if (error || !post) {
    return (
      <div className="error-container">
        <h2>Article Not Found</h2>
        <p>{error || "The article you're looking for doesn't exist or has been removed."}</p>
        <Link to="/blog" className="btn btn-primary">Back to Blog</Link>
      </div>
    );
  }
  
  return (
    <div className="blog-post-page">
      <div className="blog-post-header">
        <div className="container">
          <Link to="/blog" className="back-link">
            <i className="fas fa-arrow-left"></i> Back to All Articles
          </Link>
          <span className="blog-post-category">{post.category}</span>
          <h1 className="blog-post-title">{post.title}</h1>
          <div className="blog-post-meta">
            <span className="blog-post-date">{post.date}</span>
            <span className="blog-post-reading-time">5 min read</span>
          </div>
        </div>
      </div>
      
      <div className="container">
        <div className="blog-post-content-wrapper">
          <div className="blog-post-featured-image">
            {post.image ? (
              <img 
                src={post.image} 
                alt={post.title} 
                className="blog-post-img"
              />
            ) : (
              <div className="placeholder-image blog-post-img"></div>
            )}
          </div>
          
          <article className="blog-post-content">
            <p className="blog-post-lead">
              {post.excerpt}
            </p>
            
            {/* Render the content based on format */}
            {post.content && (
              <div 
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            )}
          </article>
          
          <div className="blog-post-author">
            <div className="author-avatar">
              <img 
                src={post.authorImage || DEFAULT_AUTHOR_IMAGE} 
                alt={post.authorName || 'Legate Team'} 
                className="author-img"
              />
            </div>
            <div className="author-info">
              <h3>{post.authorName || 'Legate Team'}</h3>
              <p className="author-role">{post.authorRole || 'Business Formation Specialist'}</p>
              <p className="author-bio">
                {post.authorBio || 'The Legate Team has years of experience helping entrepreneurs establish and grow their businesses. We specialize in business formation, compliance, and strategic planning.'}
              </p>
            </div>
          </div>
          
          <div className="blog-post-share">
            <h3>Share This Article</h3>
            <div className="share-buttons">
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="share-button facebook">
                <i className="fab fa-facebook-f"></i>
              </a>
              <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`} target="_blank" rel="noopener noreferrer" className="share-button twitter">
                <i className="fab fa-twitter"></i>
              </a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="share-button linkedin">
                <i className="fab fa-linkedin-in"></i>
              </a>
              <a href={`mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent(`Check out this article: ${window.location.href}`)}`} className="share-button email">
                <i className="fas fa-envelope"></i>
              </a>
            </div>
          </div>
        </div>
        
        {relatedPosts.length > 0 && (
          <div className="related-posts">
            <h2 className="related-posts-title">Related Articles</h2>
            <div className="related-posts-grid">
              {relatedPosts.map(relatedPost => (
                <div className="blog-card" key={relatedPost.id}>
                  <div className="blog-card-image">
                    {relatedPost.image ? (
                      <img 
                        src={relatedPost.image} 
                        alt={relatedPost.title} 
                        className="blog-img"
                      />
                    ) : (
                      <div className="placeholder-image blog-img"></div>
                    )}
                  </div>
                  <div className="blog-card-content">
                    <span className="blog-category">{relatedPost.category}</span>
                    <h3 className="blog-card-title">{relatedPost.title}</h3>
                    <p className="blog-card-date">{relatedPost.date}</p>
                    <Link to={`/blog/${relatedPost.id}`} className="blog-card-link">Read More</Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="blog-post-cta">
          <div className="cta-content">
            <h2 className="cta-title">Need Help with Your Business?</h2>
            <p className="cta-text">
              Our team of experts is ready to assist you with all aspects of business formation and growth.
            </p>
            <Link to="/service-form" className="btn btn-primary cta-button">Contact Us Today</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
